<template>
  <transition name="notification-banner-fade">
    <div
      v-if="isVisible"
      class="notification-banner"
      :class="`notification-banner--${variant}`"
    >
      <div class="notification-banner__content">
        <div
          v-if="icon"
          class="notification-banner__icon-wrapper"
          :class="`notification-banner__icon-wrapper--${variant}`"
        >
          <img
            :src="icon"
            :alt="title"
            class="notification-banner__icon"
          />
        </div>

        <div class="notification-banner__text">
          <h6
            v-if="title"
            class="notification-banner__title"
            v-text="title"
          />
          <p v-if="reason"
            class="notification-banner__description"
            v-text="$ml.get('idvNotificationNDA')"
          />
          <p
            v-if="description && !reason"
            class="notification-banner__description"
            v-text="description"
          />
        </div>
     
      </div>

      <div class="notification-banner__actions">
        <button v-if="loading" class="btn" style="background-color: #f59e0b !important; border: none;">
          <Spinner />
        </button>
        <button
          v-if="primaryAction && !loading"
          type="button"
          class="btn notification-banner__btn notification-banner__btn--primary"
          :class="`notification-banner__btn--primary-${variant}`"
          @click="handlePrimary"
          v-text="primaryAction.label"
        />
      </div>

    </div>
  </transition>
</template>

<script>
import Swal from 'sweetalert2';

export default {
  name: 'NotificationBanner',

  props: {
    icon: {
      type: String,
      default: '',
    },
    title: {
      type: String,
      required: true,
    },
    description: {
      type: String,
      default: '',
    },
    reason: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    primaryAction: {
      type: Object,
      default: null,
    },
    variant: {
      type: String,
      default: 'warning',
      validator: (value) => ['warning'].includes(value),
    },
    visible: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      dismissed: false,
    };
  },

  computed: {
    isVisible() {
      return this.visible && !this.dismissed;
    },
  },

  methods: {
    isValidFullName(value) {
      const fullNameRegex = /^[A-Za-zÀ-ÖØ-öø-ÿ]{2,}(?: [A-Za-zÀ-ÖØ-öø-ÿ]{2,})+$/;
      return fullNameRegex.test(value.trim());
    },

    async handlePrimary() {
      const result = await Swal.fire({
        title: this.$ml.get('validateProfile'),
        html: `
          <p>${this.$ml.get('idvValidationPopupAllowPopups')}</p>
          <p>${this.$ml.get('idvValidationPopupNewWindow')}</p>
          <p class="profile-validation-modal__hint">
            ${this.$ml.get('idvValidationPopupFullNameHint')}
          </p>
        `,
        input: 'text',
        inputLabel: this.$ml.get('idvValidationPopupFullNameLabel'),
        inputPlaceholder: this.$ml.get('idvValidationPopupFullNamePlaceholder'),
        inputAttributes: {
          autocomplete: 'name',
          required: 'required',
        },
        showCancelButton: true,
        confirmButtonText: this.$ml.get('validateProfile'),
        cancelButtonText: this.$ml.get('cancel'),
        reverseButtons: true,
        buttonsStyling: false,
        customClass: {
          popup: 'profile-validation-modal',
          title: 'profile-validation-modal__title',
          htmlContainer: 'profile-validation-modal__text',
          actions: 'profile-validation-modal__actions',
          confirmButton: 'btn profile-validation-modal__btn--primary',
          cancelButton: 'btn profile-validation-modal__btn--secondary',
          input: 'profile-validation-modal__input',
          inputLabel: 'profile-validation-modal__input-label',
        },
        focusConfirm: false,
        didOpen: () => {
          const confirmButton = Swal.getConfirmButton();
          const input = Swal.getInput();

          if (confirmButton) {
            confirmButton.disabled = true;
          }

          if (input) {
            input.addEventListener('input', () => {
              if (confirmButton) {
                confirmButton.disabled = !this.isValidFullName(input.value);
              }
            });
          }
        },
        preConfirm: () => {
          const input = Swal.getInput();
          const value = input ? input.value.trim() : '';
          return this.isValidFullName(value) ? value : false;
        },
      });

      if (result.isConfirmed) {
        this.$emit('primary', result.value);
      }
    },

  },
};
</script>

<style scoped>
.notification-banner {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 1.25rem;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  border-radius: 16px;
  border: 1px solid transparent;
  background-color: #f9f5fc;
  box-shadow: 0 2px 12px rgba(115, 11, 169, 0.06);
  transition: all 0.3s ease;
}


.notification-banner--warning {
  background-color: #fffbeb;
  border-color: rgba(245, 158, 11, 0.08);
}

.notification-banner__content {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  flex: 1;
}

.notification-banner__icon-wrapper {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}


.notification-banner__icon-wrapper--warning {
  background-color: rgba(245, 158, 11, 0.1);
}

.notification-banner__icon {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.notification-banner__icon-wrapper--warning .notification-banner__icon path {
  fill: #f59e0b;
}

.notification-banner__text {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  padding-right: 1.5rem;
}

.notification-banner__title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 500;
  color: #1a202c;
  line-height: 1.4;
}

.notification-banner__description {
  margin: 0;
  font-size: 0.92rem;
  color: #718096;
  line-height: 1.5;
}

.notification-banner__actions {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.5rem;
  flex-shrink: 0;
}

.notification-banner__btn {
  padding: 0.6rem 1.25rem;
  font-size: 0.9rem;
  border-radius: 8px;
  transition: all 0.2s ease;
  white-space: nowrap;
}


.notification-banner__btn--primary-warning {
  background-color: #f59e0b;
  color: #1a202c !important;
  border: none;
}

.notification-banner__btn--primary-warning:hover {
  background-color: #d97706;
}


@media (min-width: 768px) {
  .notification-banner {
    flex-direction: row;
    align-items: center;
    padding: 1.25rem 1.5rem;
  }

  .notification-banner__actions {
    flex-direction: row;
    justify-content: flex-end;
    align-items: center;
    margin-left: auto;
  }

  .notification-banner__btn {
    width: auto;
  }
}

.notification-banner-fade-enter-active,
.notification-banner-fade-leave-active {
  transition: opacity 0.35s ease, transform 0.35s ease;
}

.notification-banner-fade-enter,
.notification-banner-fade-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
</style>

<style>
.profile-validation-modal {
  padding: 2rem;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12);
}

.profile-validation-modal__title {
  margin: 0 0 1rem;
  font-size: 1.25rem;
  font-weight: 600;
  color: #1a202c;
}

.profile-validation-modal__text {
  margin: 0;
  font-size: 0.95rem;
  color: #718096;
  line-height: 1.6;
}

.profile-validation-modal__text p {
  margin: 0 0 0.75rem;
}

.profile-validation-modal__text p:last-child {
  margin-bottom: 0;
}

.profile-validation-modal__actions {
  margin-top: 1.5rem;
  gap: 0.75rem;
}

.profile-validation-modal__btn--primary {
  background-color: #730ba9;
  border-color: #730ba9;
  color: #fff !important;
  border-radius: 8px;
  padding: 0.625rem 1.25rem;
  font-size: 0.93rem;
  font-weight: 500;
}

.profile-validation-modal__btn--primary:hover {
  background-color: #5a0887;
  border-color: #5a0887;
}

.profile-validation-modal__btn--secondary {
  background-color: transparent;
  border-color: #e2e8f0;
  color: #718096 !important;
  border-radius: 8px;
  padding: 0.625rem 1.25rem;
  font-size: 0.93rem;
  font-weight: 500;
}

.profile-validation-modal__btn--secondary:hover {
  background-color: #f8fafc;
  border-color: #cbd5e1;
  color: #475569 !important;
}

.profile-validation-modal__hint {
  margin: 1.25rem 0 0.5rem;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  background-color: #fffbeb;
  border: 1px solid rgba(245, 158, 11, 0.2);
  color: #92400e;
  font-size: 0.9rem;
  font-weight: 500;
  line-height: 1.5;
}

.profile-validation-modal__input-label {
  display: block;
  margin-top: 1rem;
  margin-bottom: 0.4rem;
  font-size: 0.95rem;
  font-weight: 600;
  color: #1a202c;
}

.profile-validation-modal__input {
  width: 100%;
  padding: 0.65rem 0.9rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.95rem;
  color: #1a202c;
  background-color: #fff;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.profile-validation-modal__input:focus {
  border-color: #730ba9;
  box-shadow: 0 0 0 3px rgba(115, 11, 169, 0.12);
  outline: none;
}

.profile-validation-modal__btn--primary:disabled,
.profile-validation-modal__btn--primary:disabled:hover {
  background-color: #a0aec0;
  border-color: #a0aec0;
  cursor: not-allowed;
}
</style>
